// Shippers may relabel the carriers they partner with, so their view of the network
// doesn't expose who actually fulfills. The label lives on `partnerships.carrier_alias`,
// the mirror of `shipper_alias`.
//
// The map is built once in useClientUser from `shipper_partnerships` — partnerships where
// the logged-in client is the shipper. That makes "shippers only" structural rather than a
// role check: a carrier's is empty, so every lookup falls through to the real name.
//
// Forward stocking does not use this. That view flattens the name into a column before the
// browser sees it, so it resolves the alias in SQL instead.

// carrier_id -> the whole partnership row, so callers can reach anything on it
// (carrier_alias, share_manifests, status, the nested `carrier` client) from one lookup
// rather than a fresh `.find()` over partner_carriers each time.
//
// Rows with no carrier_id are dropped: a shipper's contractor partnerships carry
// contractor_id instead, and keying those on `undefined` would collapse the map to a single
// bogus entry. Rows with no alias are kept — they are still real partnerships, and
// carrierDisplayName falls through on a null alias anyway.
//
// (shipper_id, carrier_id) is unique in the DB — partnerships_two_of_three_check plus
// partnerships_pairing_unique_idx — so no two rows here can collide on a key.
export const buildPartnerCarrierMap = (partnerCarriers = []) =>
    Object.fromEntries(
        (partnerCarriers || [])
            .filter((partnership) => partnership?.carrier_id)
            .map((partnership) => [partnership.carrier_id, partnership])
    );

// The name to show for a carrier-shaped object ({ client_id, business_name }), preferring
// the viewer's alias. Returns null when there is neither, so callers keep their own
// placeholder ('-', 'TBD', …) rather than inheriting one from here.
//
// An empty-string alias falls through to the real name. The DB check constraint
// partnerships_carrier_alias_not_blank should keep '' out of the column, but `||` agreeing
// with SQL's COALESCE only by accident is not worth relying on.
export const carrierDisplayName = (carrier, partnersById) =>
    (carrier?.client_id && partnersById?.[carrier.client_id]?.carrier_alias) || carrier?.business_name || null;

// Carrier lists arrive from GraphQL ordered by `carrier.business_name`, which an aliased
// shipper is never shown — so a dropdown built from one looks unsorted. Re-sort by the
// resolved label instead. `pick` locates the carrier-shaped object when the list holds
// something else, such as a partnership row. Returns a new array; does not mutate.
export const sortByCarrierDisplayName = (carriers, partnersById, pick = (entry) => entry) =>
    [...(carriers || [])].sort((l, r) =>
        String(carrierDisplayName(pick(l), partnersById) || '').localeCompare(
            String(carrierDisplayName(pick(r), partnersById) || '')
        )
    );
